import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ChartContainer = ({ selectedCrypto, timeframe, indicators, onTimeframeChange }) => {
  const [chartType, setChartType] = useState('candlestick');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Mock chart data
  const mockChartData = [
    { time: '09:00', open: 43250, high: 43500, low: 43100, close: 43400, volume: 1250000 },
    { time: '09:15', open: 43400, high: 43800, low: 43300, close: 43650, volume: 1180000 },
    { time: '09:30', open: 43650, high: 43900, low: 43500, close: 43750, volume: 1320000 },
    { time: '09:45', open: 43750, high: 44100, low: 43600, close: 43950, volume: 1450000 },
    { time: '10:00', open: 43950, high: 44200, low: 43800, close: 44050, volume: 1380000 },
    { time: '10:15', open: 44050, high: 44300, low: 43900, close: 44150, volume: 1290000 },
    { time: '10:30', open: 44150, high: 44400, low: 44000, close: 44250, volume: 1410000 },
    { time: '10:45', open: 44250, high: 44500, low: 44100, close: 44350, volume: 1520000 },
    { time: '11:00', open: 44350, high: 44600, low: 44200, close: 44450, volume: 1340000 },
    { time: '11:15', open: 44450, high: 44700, low: 44300, close: 44550, volume: 1280000 },
    { time: '11:30', open: 44550, high: 44800, low: 44400, close: 44650, volume: 1360000 },
    { time: '11:45', open: 44650, high: 44900, low: 44500, close: 44750, volume: 1470000 }
  ];

  const timeframes = [
    { value: '1m', label: '1M' },
    { value: '5m', label: '5M' },
    { value: '15m', label: '15M' },
    { value: '1h', label: '1H' },
    { value: '4h', label: '4H' },
    { value: '1d', label: '1D' },
    { value: '1w', label: '1W' }
  ];

  const chartTypes = [
    { value: 'candlestick', label: 'Candlestick', icon: 'BarChart3' },
    { value: 'line', label: 'Line', icon: 'TrendingUp' },
    { value: 'area', label: 'Area', icon: 'Activity' }
  ];

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${isFullscreen ? 'fixed inset-4 z-50' : 'h-[600px]'}`}>
      {/* Chart Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <Icon name="Bitcoin" size={16} color="white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{selectedCrypto}</h3>
              <p className="text-sm text-success">$44,750.00 (+2.45%)</p>
            </div>
          </div>

          {/* Timeframe Selector */}
          <div className="flex items-center space-x-1 bg-muted rounded-md p-1">
            {timeframes?.map((tf) => (
              <button
                key={tf?.value}
                onClick={() => onTimeframeChange(tf?.value)}
                className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                  timeframe === tf?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tf?.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {/* Chart Type Selector */}
          <div className="flex items-center space-x-1 bg-muted rounded-md p-1">
            {chartTypes?.map((type) => (
              <button
                key={type?.value}
                onClick={() => setChartType(type?.value)}
                className={`flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                  chartType === type?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={type?.icon} size={14} />
                <span className="hidden sm:inline">{type?.label}</span>
              </button>
            ))}
          </div>

          <Button variant="ghost" size="sm" onClick={toggleFullscreen}>
            <Icon name={isFullscreen ? "Minimize2" : "Maximize2"} size={16} />
          </Button>
        </div>
      </div>
      {/* Chart Area */}
      <div className="p-4 h-full">
        <ResponsiveContainer width="100%" height="90%">
          <LineChart data={mockChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="time" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              domain={['dataMin - 100', 'dataMax + 100']}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'var(--color-popover)',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                color: 'var(--color-foreground)'
              }}
            />
            <Line 
              type="monotone" 
              dataKey="close" 
              stroke="var(--color-primary)" 
              strokeWidth={2}
              dot={false}
            />
            {indicators?.includes('sma20') && (
              <Line 
                type="monotone" 
                dataKey="sma20" 
                stroke="var(--color-warning)" 
                strokeWidth={1}
                dot={false}
                strokeDasharray="5 5"
              />
            )}
            {indicators?.includes('sma50') && (
              <Line 
                type="monotone" 
                dataKey="sma50" 
                stroke="var(--color-accent)" 
                strokeWidth={1}
                dot={false}
                strokeDasharray="3 3"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Drawing Tools */}
      <div className="absolute bottom-4 left-4 flex items-center space-x-2">
        <Button variant="ghost" size="sm">
          <Icon name="Minus" size={16} />
        </Button>
        <Button variant="ghost" size="sm">
          <Icon name="TrendingUp" size={16} />
        </Button>
        <Button variant="ghost" size="sm">
          <Icon name="Square" size={16} />
        </Button>
        <Button variant="ghost" size="sm">
          <Icon name="Circle" size={16} />
        </Button>
      </div>
    </div>
  );
};

export default ChartContainer;